import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectNumberData, type NumberInfoProps } from '../store/map';
import { useCustomNavigate } from './useCustomNavigate';
import { useNextPageName } from './useNextPageName';

export const SEARCHING_DURATION = 10000;
const PROGRESS_STEPS = 100;

interface UseSearchingScreenOptions {
  duration?: number;
}

export const useSearchingScreen = ({
  duration = SEARCHING_DURATION,
}: UseSearchingScreenOptions = {}) => {
  const navigate = useCustomNavigate();
  const nextPage = useNextPageName();
  const numberInfo: NumberInfoProps = useSelector(selectNumberData);
  const [progress, setProgress] = useState(0);

  const hasPhoneNumber = Boolean(numberInfo?.phone_number);
  const isSearching = progress < PROGRESS_STEPS;

  useEffect(() => {
    if (!hasPhoneNumber) navigate(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPhoneNumber, nextPage]);

  useEffect(() => {
    if (!hasPhoneNumber || !isSearching) return;

    const intervalId = setInterval(() => {
      setProgress((current) => Math.min(current + 1, PROGRESS_STEPS));
    }, duration / PROGRESS_STEPS);

    return () => clearInterval(intervalId);
  }, [hasPhoneNumber, isSearching, duration]);

  const goToNextPage = useCallback(() => navigate(nextPage), [navigate, nextPage]);

  return {
    numberInfo,
    hasPhoneNumber,
    lat: numberInfo?.latitude ?? null,
    lng: numberInfo?.longitude ?? null,
    progress,
    isSearching,
    nextPage,
    goToNextPage,
  };
};
